import { Link } from "react-router-dom";
import { Avatar, Box, Skeleton, Typography, IconButton } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

import { User } from "../../../../../types/types";

import { VerificationIcon } from "../../../../User/assets/icons/user-info-icons";
import { useUserDataFormatter } from "../../../../User/hooks/useUserDataFormatter";

const ChatContainerHeader = ({
  senderUserData,
  isChatLoading,
  onBackToSidebar,
  showBackButton,
}: {
  senderUserData?: User;
  isChatLoading: boolean;
  onBackToSidebar?: () => void;
  showBackButton?: boolean;
}) => {
  const { fullName, firstTwoChr, media, isVerified, profileLink, formattedInfo } = useUserDataFormatter(senderUserData);

  if (isChatLoading) return <ChatContainerHeaderLoader />;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.topSection}>
        {showBackButton && (
          <IconButton onClick={onBackToSidebar} sx={styles.backButton} aria-label="Back to chat list">
            <ArrowBack />
          </IconButton>
        )}
        <Box sx={styles.avatarWrapper}>
          <Avatar src={media} sx={styles.avatar}>
            {firstTwoChr}
          </Avatar>
          {isVerified && <VerificationIcon sx={styles.verificationIcon} />}
        </Box>

        <Box sx={styles.infoWrapper}>
          <Typography sx={styles.fullName} component={Link} to={profileLink}>
            {fullName}
          </Typography>
          <Typography sx={styles.subInfo}>{formattedInfo(",")}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ChatContainerHeaderLoader = () => {
  return (
    <Box sx={styles.container}>
      <Box sx={styles.topSection}>
        <Box sx={styles.avatarWrapper}>
          <Skeleton variant="circular" width={48} sx={styles.avatar} />
        </Box>

        <Box sx={styles.infoWrapper}>
          <Skeleton variant="text" width={160} height={20} />
          <Skeleton variant="text" width={100} height={16} />
        </Box>
      </Box>
    </Box>
  );
};

export default ChatContainerHeader;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    rowGap: "5px",
    padding: {
      xs: "20px 16px 12px", // Reduced padding on mobile
      md: "30px 25px 9px", // Original padding on desktop
    },
    width: "100%",
    backgroundColor: {
      xs: "#FFFFFF", // White background on mobile
      md: "transparent", // Transparent on desktop
    },
    borderBottom: {
      xs: "1px solid #EAECF0", // Bottom border on mobile for separation
      md: "none", // No border on desktop
    },
    // Add subtle shadow on mobile
    boxShadow: {
      xs: "0 2px 4px rgba(0, 0, 0, 0.05)", // Light shadow on mobile
      md: "none", // No shadow on desktop
    },
  },
  topSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },
  backButton: {
    marginRight: "8px",
    color: "#008B07",
    padding: "8px",
    backgroundColor: {
      xs: "#F8F9FA", // Light background on mobile
      md: "transparent", // Transparent on desktop
    },
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: {
        xs: "#E9ECEF", // Darker background on hover for mobile
        md: "rgba(0, 139, 7, 0.1)", // Original hover on desktop
      },
    },
  },
  avatarWrapper: {
    position: "relative",
  },
  avatar: {
    width: "48px",
    height: "48px",
  },
  verificationIcon: {
    position: "absolute",
    bottom: "-4px",
    left: "50%",
    transform: "translateX(-50%)",
  },
  infoWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  fullName: {
    color: "var(--Gray-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    fontFamily: "Montserrat",
    textDecoration: "none",
  },
  subInfo: {
    color: "var(--Dark---1000, #111)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    fontFamily: "Montserrat",
  },
};
